import * as React from 'react';
import { FixMeLater } from '../../utils';
import { Styler, StylerBaseProps } from '../Styler';
import { STATIC_STYLES } from './ImageBase.styles';
import { useEventCallback, useForkRef, useIsomorphicEffect, useLazyImage } from '../../hooks';

export interface ImageBaseProps extends StylerBaseProps {
    /**
     * The `src` attribute of the image. Not relevant if the image is the child of a picture element.
     */
    src?: string;

    /**
     * The `srcset` attribute of the image.
     */
    srcset?: string;

    /**
     * The image alt text. This will also be used as the title for the placeholder if the image was not loaded.
     */
    alt?: string;

    /**
     * Set to `true` if the image is the child of a `picture` element.
     */
    isPicture?: boolean;

    /**
     * Callback fired when the image has loaded.
     */
    onImageLoaded?: (url: string) => void;

    /**
     * Callback fired if the image fails to load.
     */
    onImageError?: () => void;

    /**
     * If `true`, the image will not lazy load when nested in a `LazyImageProvider`.
     * @default false
     */
    disableLazy?: boolean;
}

export const ImageBase: React.ForwardRefExoticComponent<ImageBaseProps & React.RefAttributes<HTMLElement>> =
    React.forwardRef(function ImageBase(props: ImageBaseProps, ref: React.ForwardedRef<FixMeLater>): React.JSX.Element {
        const { src, srcset, alt, onImageLoaded, onImageError, isPicture, disableLazy, ...other } = props;

        const [lazyImageRef] = useLazyImage();
        const isLazy = !disableLazy && lazyImageRef !== null;

        const imageRef = React.useRef<HTMLImageElement | null>(null);
        const handleRefIntermediary = useForkRef(imageRef, lazyImageRef);
        const handleRef = useForkRef(handleRefIntermediary, ref);

        const [imageLoaded, setImageLoaded] = React.useState(false);

        const handleImageLoaded = useEventCallback((event?: React.SyntheticEvent<HTMLImageElement>) => {
            setImageLoaded(true);
            const target = event?.currentTarget ?? imageRef.current;
            const finalUrl = target?.currentSrc || target?.src;
            if (finalUrl) {
                onImageLoaded?.(finalUrl);
            }
        });

        const handleImageError = useEventCallback(() => {
            onImageError?.();
        });

        useIsomorphicEffect(() => {
            if (!isLazy && imageRef.current?.complete) {
                // Defer to next frame to allow CSS transitions
                setTimeout(() => {
                    handleImageLoaded();
                }, 100);
            }
        }, []);

        return (
            <Styler
                ref={handleRef}
                component="img"
                baseStyles={STATIC_STYLES}
                // Lazy-load path
                data-src={isLazy && !isPicture ? src : undefined}
                data-srcset={isLazy && !isPicture ? srcset : undefined}
                // Eager path
                src={!isLazy && !isPicture ? src : undefined}
                srcSet={!isLazy && !isPicture ? srcset : undefined}
                data-in-picture={isPicture || undefined}
                data-loaded={imageLoaded || undefined}
                alt={alt || undefined}
                onLoad={handleImageLoaded}
                onError={handleImageError}
                data-image={true}
                {...other}
            />
        );
    });
